import { z } from 'zod'
import { type Coordinates, distanceMeters } from './location'

export const deckItemTypeValues = ['event', 'listing', 'idea'] as const
export const deckClosesInMinutesValues = [60, 180, 1440] as const
export const deckResolutionValues = [
  'all_swiped',
  'deadline',
  'closed_early',
  'no_match',
  'runoff',
  'match',
  'tie',
  'picked',
] as const

export const DECK_IDEA_TITLE_MAX = 100
export const DECK_IDEA_DESCRIPTION_MAX = 300
export const DECK_TITLE_MAX = 120

/** One shared item inside a swipe-deck send (2..7 per deck): an event or listing by id,
 * or a free idea card (brainstorming) with a title and an optional description. */
export const deckIdeaWhereSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  street: z.string().max(200).nullable(),
  city: z.string().max(100).nullable(),
})

export const deckItemInputSchema = z.union([
  z.object({
    type: z.enum(['event', 'listing']),
    id: z.string().uuid(),
  }),
  z.object({
    type: z.literal('idea'),
    title: z.string().trim().min(1).max(DECK_IDEA_TITLE_MAX),
    description: z.string().trim().max(DECK_IDEA_DESCRIPTION_MAX).optional(),
    /** The real place an idea stands for, when it came from the map. */
    where: deckIdeaWhereSchema.optional(),
  }),
])

export type DeckItemInput = z.infer<typeof deckItemInputSchema>

export const DECK_MAX_OPTIONS = 7

export const deckItemsInputSchema = z.array(deckItemInputSchema).min(2).max(DECK_MAX_OPTIONS)

/** The cards a plan deck starts with: an instant deck needs two, a collecting one may start empty. */
export const deckSeedItemsInputSchema = z.array(deckItemInputSchema).max(DECK_MAX_OPTIONS)

/**
 * How a plan deck fills: `instant` opens for swiping right away, `collect` gathers cards from
 * the group until its starter or the plan's creator opens it (or it holds seven).
 */
export const deckModeValues = ['instant', 'collect'] as const

export const deckModeSchema = z.enum(deckModeValues)

export type DeckMode = z.infer<typeof deckModeSchema>

/** Optional heading of a deck ("Was machen wir am Samstag?"), shown on the chat card and above the cards. */
export const deckTitleInputSchema = z.string().trim().min(1).max(DECK_TITLE_MAX)

export const deckClosesInMinutesSchema = z.union([z.literal(60), z.literal(180), z.literal(1440)])

/** POST /api/decks/{deckId}/start — open a collecting deck for swiping. */
export const deckStartInputSchema = z.object({
  closesInMinutes: deckClosesInMinutesSchema.optional(),
})

export type DeckStartInput = z.infer<typeof deckStartInputSchema>

/** POST /api/decks/{deckId}/pick — connect one option of a closed deck. */
export const deckPickInputSchema = z.object({ optionId: z.string().uuid() })

export type DeckPickInput = z.infer<typeof deckPickInputSchema>

/**
 * Display payload carried by a `swipe_deck` chat message. Never holds swipe
 * state — the card reads live state from GET /api/decks/{deckId}, so the
 * message can not go stale (the `plan_vote` doctrine).
 */
export const swipeDeckMessageMetadataSchema = z.object({
  deckId: z.string().uuid(),
  optionCount: z.number().int(),
  covers: z.array(z.string()).max(3),
  closesAt: z.string(),
  /** Heading the sender gave the deck; absent on decks without one. */
  title: z.string().optional(),
  /** True when every card is a free idea (brainstorming deck). */
  ideas: z.boolean().optional(),
})

export type SwipeDeckMessageMetadata = z.infer<typeof swipeDeckMessageMetadataSchema>

/** One opening span of a place on a weekday (0 = Sunday), local "HH:mm"; a close at or before
 * the open runs past midnight. */
export const deckOpeningHoursSchema = z.object({
  day: z.number().int().min(0).max(6),
  open: z.string(),
  close: z.string(),
})

export type DeckOpeningHours = z.infer<typeof deckOpeningHoursSchema>

/** One deck option with its creation-time snapshot; `voteCount` stays null
 * while the deck is open (blind counts) and is revealed on resolve. */
export const deckOptionDtoSchema = z.object({
  id: z.string().uuid(),
  position: z.number().int(),
  entityType: z.enum(deckItemTypeValues),
  entityId: z.string().uuid().nullable(),
  title: z.string(),
  coverImageUrl: z.string().nullable(),
  locationName: z.string().nullable(),
  description: z.string().nullable(),
  voteCount: z.number().int().nullable(),
  /** When an event card's event starts (ISO); null for places and ideas. */
  startsAt: z.string().nullable().optional(),
  /** A place's category tag ("FoodAndDrinks"), named by the client; null without one. */
  categoryName: z.string().nullable().optional(),
  /** Street and town of a place or an event's venue; empty or null when unknown. */
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  /** A place's average rating; null while it has no reviews. */
  rating: z.number().nullable().optional(),
  /** When a place is open through the week; null when its hours are unknown. */
  openingHours: z.array(deckOpeningHoursSchema).nullable().optional(),
})

export type DeckOptionDto = z.infer<typeof deckOptionDtoSchema>

export type DeckOpening = 'open' | 'closed' | 'closedThatDay'

export type DeckOptionFacts = {
  /** An event's venue, else the street, with the town. */
  place: string | null
  /** From the plan's place, else from the viewer; null while either end is unknown. */
  distanceMeters: number | null
  /** Whether a place is open when the plan starts; null without its hours or the plan's day. */
  opening: DeckOpening | null
}

type DeckFactsPlan = {
  lat: number | null
  lng: number | null
  date: string | null
  timeStart: string | null
}

function clockMinutes(time: string): number | null {
  const clock = /^(\d{1,2}):(\d{2})/.exec(time)
  return clock ? Number(clock[1]) * 60 + Number(clock[2]) : null
}

function openingAt(
  hours: DeckOpeningHours[] | null | undefined,
  date: string | null,
  time: string | null,
): DeckOpening | null {
  if (!hours || hours.length === 0 || !date) return null
  const day = new Date(`${date.slice(0, 10)}T12:00:00Z`).getUTCDay()
  if (Number.isNaN(day)) return null
  const sameDay = hours.filter((span) => span.day === day)
  const at = time ? clockMinutes(time) : null
  if (at == null) return sameDay.length === 0 ? 'closedThatDay' : null
  const spans = (list: DeckOpeningHours[]) =>
    list.flatMap((span) => {
      const open = clockMinutes(span.open)
      const close = clockMinutes(span.close)
      return open == null || close == null ? [] : [{ open, close }]
    })
  const lateNight = spans(hours.filter((span) => span.day === (day + 6) % 7)).some(
    ({ open, close }) => close <= open && at < close,
  )
  const open = spans(sameDay).some(({ open, close }) =>
    close <= open ? at >= open : at >= open && at < close,
  )
  return lateNight || open ? 'open' : 'closed'
}

/** What a swipe card says beside its title: where the place is, how far, and if it is open then. */
export function deckOptionFacts(
  option: DeckOptionDto,
  context: { plan?: DeckFactsPlan | null; viewer?: Coordinates | null },
): DeckOptionFacts {
  const city = option.city?.trim() || null
  const spot =
    (option.entityType === 'event' ? option.locationName?.trim() : null) ||
    option.street?.trim() ||
    null
  const plan = context.plan
  const origin =
    plan?.lat != null && plan.lng != null ? { lat: plan.lat, lng: plan.lng } : context.viewer
  const here =
    option.lat != null && option.lng != null ? { lat: option.lat, lng: option.lng } : null
  return {
    place: [spot, city].filter(Boolean).join(', ') || null,
    distanceMeters: origin && here ? distanceMeters(origin, here) : null,
    opening: openingAt(option.openingHours, plan?.date ?? null, plan?.timeStart ?? null),
  }
}

/** The viewer's ballot: which options they saw and whether they finished the deck. */
export const deckBallotDtoSchema = z.object({
  seenOptionIds: z.array(z.string().uuid()),
  completedAt: z.string().nullable(),
  skipped: z.boolean(),
})

export type DeckBallotDto = z.infer<typeof deckBallotDtoSchema>

export const deckProgressSchema = z.object({
  requiredCount: z.number().int(),
  completedCount: z.number().int(),
})

export type DeckProgress = z.infer<typeof deckProgressSchema>

export const deckDtoSchema = z.object({
  id: z.string().uuid(),
  scope: z.enum(['conversation', 'plan']),
  conversationId: z.string().uuid().nullable(),
  planId: z.string().uuid().nullable(),
  createdById: z.string().uuid().nullable(),
  /** Heading the sender gave the deck, or null. */
  title: z.string().nullable(),
  /** `collecting` gathers options before anyone swipes; its starter opens it. */
  status: z.enum(['collecting', 'open', 'resolved']),
  resolution: z.enum(deckResolutionValues).nullable(),
  winnerOptionId: z.string().uuid().nullable(),
  /** Plan the backend created from the winner of a chat deck, once resolved. */
  resultPlanId: z.string().uuid().nullable(),
  closesAt: z.string(),
  createdAt: z.string(),
  allowParticipantOptions: z.boolean(),
  options: z.array(deckOptionDtoSchema),
  mine: deckBallotDtoSchema.nullable(),
  progress: deckProgressSchema,
  isMatchMode: z.boolean(),
  /** The plan assistant's "where do we go" pick: the winning listing becomes the plan's place. */
  placePick: z.boolean().optional(),
  /** A tie-break deck from before tied decks were left for an editor to pick. */
  runoff: z.boolean().optional(),
})

export type DeckDto = z.infer<typeof deckDtoSchema>

/** The viewer can still swipe: the deck is open, they hold a ballot and cards are left. */
export function deckNeedsMySwipe(deck: DeckDto): boolean {
  if (deck.status !== 'open' || deck.mine == null || deck.mine.skipped) return false
  const seen = new Set(deck.mine.seenOptionIds)
  return deck.options.some((option) => !seen.has(option.id))
}

/**
 * Orders a plan's decks for display. Open decks come first, the ones still waiting for the
 * viewer's swipes ahead of the rest, then by soonest deadline. Results follow newest first;
 * an older deck that handed its tie to a runoff deck is left out, the runoff carries on.
 */
export function planDeckSections(
  decks: DeckDto[],
  maxResolved = 2,
): { open: DeckDto[]; resolved: DeckDto[]; hiddenResolved: number } {
  const open = decks
    .filter((deck) => deck.status !== 'resolved')
    .sort(
      (a, b) =>
        Number(deckNeedsMySwipe(b)) - Number(deckNeedsMySwipe(a)) ||
        Date.parse(a.closesAt) - Date.parse(b.closesAt),
    )
  const results = decks
    .filter((deck) => deck.status === 'resolved' && deck.resolution !== 'runoff')
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
  return {
    open,
    resolved: results.slice(0, maxResolved),
    hiddenResolved: Math.max(0, results.length - maxResolved),
  }
}

/** GET /api/plans/{id}/decks — the plan's decks, open ones first, so late joiners and the
 * plan info page can reach a deck without its chat message. */
export const planDecksResponseSchema = z.object({
  decks: z.array(deckDtoSchema),
})

export type PlanDecksResponse = z.infer<typeof planDecksResponseSchema>

export const deckResponseSchema = z.object({
  deck: deckDtoSchema,
})

export type DeckResponse = z.infer<typeof deckResponseSchema>

/**
 * Several decks in one round trip, for a chat page that holds many swipe cards.
 * Decks the viewer may not see are omitted rather than erroring, so one stale id
 * in a message page cannot fail the whole batch.
 */
export const decksBatchResponseSchema = z.object({
  decks: z.array(deckDtoSchema),
})

export type DecksBatchResponse = z.infer<typeof decksBatchResponseSchema>

export const deckSwipeInputSchema = z.object({
  optionId: z.string().uuid(),
  direction: z.enum(['left', 'right']),
})

export type DeckSwipeInput = z.infer<typeof deckSwipeInputSchema>
